import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type BenutzerEingabe, type BenutzerZeile } from "../api";
import { PersonSucher } from "../komponenten/PersonSucher";
import { useTitel } from "../titel";

/**
 * Benutzerverwaltung.
 *
 * Vorher ging das nur mit `npm run benutzer` am Pi. Was hier steht, ist
 * genau das, was die Tabelle `benutzer` hergibt — Rolle, verknüpfte Person
 * und die drei Regeln, die darüber entscheiden, wer wen sieht.
 *
 * Passwörter kommen hier nicht vor. Der Besitzer erzeugt einen
 * Einladungslink, die Person setzt ihr Passwort selbst.
 */

const ROLLEN = ["besitzer", "bearbeiter", "betrachter"] as const;
type Rolle = (typeof ROLLEN)[number];

const ROLLE_KURZ: Record<Rolle, string> = {
  besitzer: "Besitzer",
  bearbeiter: "Bearbeiter",
  betrachter: "Betrachter",
};

const feld =
  "rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none " +
  "focus:border-black/40 dark:border-white/15 dark:bg-neutral-800";

const knopf =
  "rounded-lg border border-black/15 px-3 py-2 text-sm hover:border-black/40 " +
  "disabled:opacity-40 dark:border-white/15";

/** Was im Formular stehen kann, inklusive der beiden Sonderfelder. */
type Aenderung = Partial<BenutzerEingabe> & { aktiv?: boolean; passwort?: string };

function datumKnapp(wert: string | null) {
  if (!wert) return "noch nie";
  return new Date(wert).toLocaleDateString("de-AT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Zahlenfeld, das ein leeres Feld NICHT als 0 durchlässt.
 *
 * `Number("")` ist 0, nicht NaN. Wer die Ziffer löscht, um eine andere zu
 * tippen, hätte damit zwischendurch Grad 0 gespeichert — und Grad 0 heißt:
 * dieser Zugang sieht niemanden mehr.
 */
function Zahl({
  wert,
  min,
  max,
  aus,
  setzen,
}: {
  wert: number;
  min: number;
  max: number;
  aus?: boolean;
  setzen: (n: number) => void;
}) {
  const [text, setText] = useState<string | null>(null);
  return (
    <input
      type="number"
      min={min}
      max={max}
      disabled={aus}
      value={text ?? wert}
      onChange={(e) => {
        setText(e.target.value);
        if (e.target.value !== "") {
          const n = Number(e.target.value);
          if (Number.isFinite(n) && n >= min && n <= max) setzen(n);
        }
      }}
      onBlur={() => setText(null)}
      className={feld + " w-20"}
    />
  );
}

/**
 * Der Einladungslink, als Overlay über der Seite.
 *
 * Er liegt bewusst im Zustand der SEITE und nicht in der Zeile, aus der er
 * entstanden ist: das Token existiert genau einmal, in der Antwort des
 * Servers. Klappte die Zeile zu — durch „speichern", durch einen Klick auf
 * eine andere Zeile — nähme sie den Link mit, und übrig bliebe ein
 * gültiger Zugangsschlüssel, den niemand kennt.
 */
function Einladungslink({
  token,
  name,
  onFertig,
}: {
  token: string;
  name: string;
  onFertig: () => void;
}) {
  const [kopiert, setKopiert] = useState(false);
  const adresse = `${window.location.origin}/einladung/${token}`;

  async function kopieren() {
    try {
      await navigator.clipboard.writeText(adresse);
      setKopiert(true);
    } catch {
      // Ohne HTTPS oder mit strengen Browsereinstellungen gibt es keine
      // Zwischenablage. Dann bleibt das Feld zum Markieren.
      setKopiert(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-xl border border-black/10 bg-white p-5 shadow-xl dark:border-white/10 dark:bg-neutral-900">
        <h2 className="font-medium">Einladungslink für {name}</h2>
        <p className="mt-2 text-sm text-black/60 dark:text-white/60">
          Dieser Link wird <strong>nur dieses eine Mal angezeigt</strong>. Gespeichert ist davon
          nur ein Hash — er lässt sich nicht wieder hervorholen. Weitergeben (Telefon, Signal,
          Zettel), dann schließen. Gilt sieben Tage und nur einmal.
        </p>
        <div className="mt-3 flex gap-2">
          <input
            readOnly
            autoFocus
            value={adresse}
            onFocus={(e) => e.target.select()}
            className={feld + " flex-1 font-mono text-xs"}
          />
          <button onClick={kopieren} className={knopf}>
            {kopiert ? "kopiert" : "kopieren"}
          </button>
        </div>
        <div className="mt-4 text-right">
          <button onClick={onFertig} className={knopf}>
            schließen
          </button>
        </div>
      </div>
    </div>
  );
}

/** Die Sichtbarkeitsregeln eines Zugangs — dieselben vier Werte wie im Skript. */
function Regeln({
  werte,
  setzen,
}: {
  werte: Pick<
    BenutzerZeile,
    "maxBlutsgrad" | "schwiegerSichtbar" | "maxSchwiegergrad" | "verstorbeneSichtbar"
  >;
  setzen: (teil: Aenderung) => void;
}) {
  return (
    <div className="space-y-2">
      <label className="flex flex-wrap items-center gap-2 text-sm">
        <span className="w-52 text-black/60 dark:text-white/60">Verwandtschaft bis Grad</span>
        <Zahl
          wert={werte.maxBlutsgrad}
          min={0}
          max={12}
          setzen={(n) => setzen({ maxBlutsgrad: n })}
        />
        <span className="text-xs text-black/40 dark:text-white/40">
          1 = Eltern und Kinder, 2 = Großeltern und Geschwister, 3 = Cousins
        </span>
      </label>
      <label className="flex flex-wrap items-center gap-2 text-sm">
        <span className="w-52 text-black/60 dark:text-white/60">Angeheiratete sichtbar</span>
        <input
          type="checkbox"
          checked={werte.schwiegerSichtbar}
          onChange={(e) => setzen({ schwiegerSichtbar: e.target.checked })}
        />
        <span className="ml-4 text-black/60 dark:text-white/60">davon Schritte</span>
        <Zahl
          wert={werte.maxSchwiegergrad}
          min={0}
          max={4}
          aus={!werte.schwiegerSichtbar}
          setzen={(n) => setzen({ maxSchwiegergrad: n })}
        />
      </label>
      <label className="flex flex-wrap items-center gap-2 text-sm">
        <span className="w-52 text-black/60 dark:text-white/60">Längst Verstorbene sichtbar</span>
        <input
          type="checkbox"
          checked={werte.verstorbeneSichtbar}
          onChange={(e) => setzen({ verstorbeneSichtbar: e.target.checked })}
        />
        <span className="text-xs text-black/40 dark:text-white/40">
          vor über 100 Jahren geboren oder vor über 25 Jahren gestorben — unabhängig von der
          Verwandtschaft
        </span>
      </label>
    </div>
  );
}

/**
 * Der Bearbeitungsradius — derselbe Zuschnitt noch einmal, aber fürs
 * Ändern. Die Obergrenzen kommen aus den Lesewerten darüber; die API
 * begrenzt zusätzlich beim Speichern, damit eine Zahl aus HeidiSQL nicht
 * mehr verspricht als die Sicht hergibt.
 */
function RegelnBearbeiten({
  werte,
  setzen,
}: {
  werte: Pick<
    BenutzerZeile,
    | "maxBlutsgrad"
    | "schwiegerSichtbar"
    | "maxSchwiegergrad"
    | "bearbeitenBlutsgrad"
    | "bearbeitenSchwiegergrad"
    | "bearbeitenVerstorbene"
  >;
  setzen: (teil: Aenderung) => void;
}) {
  return (
    <div className="mt-4 space-y-2 border-t border-black/10 pt-3 dark:border-white/10">
      <p className="text-sm font-medium">Ändern darf er …</p>
      <label className="flex flex-wrap items-center gap-2 text-sm">
        <span className="w-52 text-black/60 dark:text-white/60">Lebende bis Grad</span>
        <Zahl
          wert={werte.bearbeitenBlutsgrad}
          min={0}
          max={werte.maxBlutsgrad}
          setzen={(n) => setzen({ bearbeitenBlutsgrad: n })}
        />
        <span className="text-xs text-black/40 dark:text-white/40">
          0 = keine Lebenden außer sich selbst · höchstens {werte.maxBlutsgrad} (der Lesegrad)
        </span>
      </label>
      <label className="flex flex-wrap items-center gap-2 text-sm">
        <span className="w-52 text-black/60 dark:text-white/60">davon Schwieger-Schritte</span>
        <Zahl
          wert={werte.bearbeitenSchwiegergrad}
          min={0}
          max={werte.schwiegerSichtbar ? werte.maxSchwiegergrad : 0}
          aus={!werte.schwiegerSichtbar}
          setzen={(n) => setzen({ bearbeitenSchwiegergrad: n })}
        />
      </label>
      <label className="flex flex-wrap items-center gap-2 text-sm">
        <span className="w-52 text-black/60 dark:text-white/60">Verstorbene</span>
        <input
          type="checkbox"
          checked={werte.bearbeitenVerstorbene}
          onChange={(e) => setzen({ bearbeitenVerstorbene: e.target.checked })}
        />
        <span className="text-xs text-black/40 dark:text-white/40">
          alle, die er sieht — abgedreht liest er die alten Linien nur
        </span>
      </label>
      <p className="text-xs text-black/40 dark:text-white/40">
        Wer eine lebende Person ändern darf, sieht ihren Eintrag ungekürzt — mit Notiz, Wohnort
        und genauem Geburtsdatum. Anders ginge es nicht: aus einem gekürzten Formular würde das
        Speichern Spalten löschen.
      </p>
    </div>
  );
}

/** Eine Zeile, aufgeklappt zum Bearbeiten. */
function Bearbeiten({
  b,
  istIch,
  onToken,
  onFertig,
}: {
  b: BenutzerZeile;
  istIch: boolean;
  onToken: (token: string, name: string) => void;
  onFertig: () => void;
}) {
  const client = useQueryClient();
  const [werte, setWerte] = useState<Aenderung>({});
  const [sucher, setSucher] = useState(false);

  // Was gerade im Formular steht: die Änderung, sonst der Stand vom Server.
  const jetzt = { ...b, ...werte };
  const geaendert = Object.keys(werte).filter((k) => k !== "passwort").length > 0;
  // Die eigene Anmelde-Adresse nur mit dem eigenen Passwort — dieselbe
  // Bedingung prüft der Server, hier nur, damit der Knopf nicht ins Leere
  // führt.
  const brauchtPasswort = istIch && werte.email !== undefined;

  const speichern = useMutation({
    mutationFn: () => api.benutzerAendern(b.id, werte),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["benutzerListe"] });
      void client.invalidateQueries({ queryKey: ["ich"] });
      setWerte({});
      onFertig();
    },
  });

  const umschalten = useMutation({
    mutationFn: (aktiv: boolean) => api.benutzerAendern(b.id, { aktiv }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["benutzerListe"] }),
  });

  const einladen = useMutation({
    mutationFn: () => api.einladungErzeugen(b.id),
    onSuccess: (d) => {
      onToken(d.token, b.anzeigename);
      void client.invalidateQueries({ queryKey: ["benutzerListe"] });
    },
  });

  const setzen = (teil: Aenderung) => setWerte((alt) => ({ ...alt, ...teil }));

  return (
    <div className="border-t border-black/10 bg-black/[0.02] p-4 dark:border-white/10 dark:bg-white/5">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-black/60 dark:text-white/60">Anzeigename</span>
          <input
            value={jetzt.anzeigename}
            onChange={(e) => setzen({ anzeigename: e.target.value })}
            className={feld + " mt-1 w-full"}
          />
        </label>
        <label className="block text-sm">
          <span className="text-black/60 dark:text-white/60">E-Mail (damit meldet er sich an)</span>
          <input
            type="email"
            value={jetzt.email}
            onChange={(e) => setzen({ email: e.target.value })}
            className={feld + " mt-1 w-full"}
          />
        </label>
        <label className="block text-sm">
          <span className="text-black/60 dark:text-white/60">Rolle</span>
          <select
            value={jetzt.rolle}
            onChange={(e) => setzen({ rolle: e.target.value as Rolle })}
            className={feld + " mt-1 w-full"}
          >
            {ROLLEN.map((r) => (
              <option key={r} value={r}>
                {ROLLE_KURZ[r]}
              </option>
            ))}
          </select>
        </label>
        <div className="text-sm">
          <span className="text-black/60 dark:text-white/60">Person im Stammbaum</span>
          <div className="mt-1 flex items-center gap-2">
            {jetzt.personId ? (
              <Link to={`/person/${jetzt.personId}`} className="underline">
                {werte.personId !== undefined
                  ? `Person ${jetzt.personId}`
                  : `${b.person?.vorname ?? ""} ${b.person?.famname ?? ""}`.trim() ||
                    `Person ${jetzt.personId}`}
              </Link>
            ) : (
              <span className="text-black/40 dark:text-white/40">keine</span>
            )}
            <button onClick={() => setSucher(!sucher)} className={knopf}>
              {jetzt.personId ? "ändern" : "verknüpfen"}
            </button>
            {jetzt.personId && (
              <button onClick={() => setzen({ personId: null })} className={knopf}>
                lösen
              </button>
            )}
          </div>
          {sucher && (
            <div className="mt-2">
              <PersonSucher
                onWahl={(p) => {
                  setzen({ personId: p.id });
                  setSucher(false);
                }}
                onAbbrechen={() => setSucher(false)}
              />
            </div>
          )}
          <p className="mt-1 text-xs text-black/40 dark:text-white/40">
            Ohne verknüpfte Person sieht dieser Zugang nur, was er selbst angelegt hat.
          </p>
        </div>
      </div>

      <div className="mt-4">
        <Regeln werte={jetzt} setzen={setzen} />
        {/* Nur bei dieser Rolle: ein Betrachter darf nichts ändern, und
            die Zahlen hätten dort nur eine Wirkung — sie heben die
            Kürzung auf. Der Server rechnet die Menge deshalb auch nur
            für Bearbeiter aus. */}
        {jetzt.rolle === "bearbeiter" && <RegelnBearbeiten werte={jetzt} setzen={setzen} />}
      </div>

      {brauchtPasswort && (
        <label className="mt-4 block max-w-xs text-sm">
          <span className="text-black/60 dark:text-white/60">
            Dein eigenes Passwort zur Bestätigung
          </span>
          <input
            type="password"
            autoComplete="current-password"
            value={werte.passwort ?? ""}
            onChange={(e) => setzen({ passwort: e.target.value })}
            className={feld + " mt-1 w-full"}
          />
          <span className="mt-1 block text-xs text-black/40 dark:text-white/40">
            Du änderst deine eigene Anmelde-Adresse. Ein Tippfehler darin sperrt dich aus, sobald
            die Sitzung abläuft.
          </span>
        </label>
      )}

      {speichern.error && (
        <p className="mt-3 text-sm text-red-700">{(speichern.error as Error).message}</p>
      )}
      {umschalten.error && (
        <p className="mt-3 text-sm text-red-700">{(umschalten.error as Error).message}</p>
      )}
      {einladen.error && (
        <p className="mt-3 text-sm text-red-700">{(einladen.error as Error).message}</p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={() => speichern.mutate()}
          disabled={
            !geaendert || speichern.isPending || (brauchtPasswort && !werte.passwort)
          }
          className="rounded-lg bg-black px-3 py-2 text-sm text-white disabled:opacity-40 dark:bg-white dark:text-black"
        >
          {speichern.isPending ? "speichert …" : "speichern"}
        </button>
        <button
          onClick={() => {
            setWerte({});
            onFertig();
          }}
          className={knopf}
        >
          abbrechen
        </button>
        <span className="flex-1" />
        {/* Für den eigenen Zugang gibt es das nicht: das wäre der Weg um
            die Passwortabfrage unter „Mein Zugang" herum. */}
        {!istIch && (
          <button
            onClick={() => einladen.mutate()}
            disabled={einladen.isPending || !b.aktiv}
            className={knopf}
            title={
              b.hatPasswort
                ? "Setzt das Passwort zurück: der Zugang bleibt, die Person setzt ein neues Passwort selbst."
                : "Erzeugt den Link, mit dem die Person ihr Passwort setzt."
            }
          >
            {b.hatPasswort ? "Passwort zurücksetzen" : "Einladungslink erzeugen"}
          </button>
        )}
        {!istIch && (
          <button
            onClick={() => umschalten.mutate(!b.aktiv)}
            disabled={umschalten.isPending}
            className={knopf}
          >
            {b.aktiv ? "abschalten" : "wieder einschalten"}
          </button>
        )}
      </div>
    </div>
  );
}

function Anlegen({
  onToken,
  onFertig,
}: {
  onToken: (token: string, name: string) => void;
  onFertig: () => void;
}) {
  const client = useQueryClient();
  const [werte, setWerte] = useState<BenutzerEingabe>({
    email: "",
    anzeigename: "",
    rolle: "betrachter",
    personId: null,
    maxBlutsgrad: 3,
    schwiegerSichtbar: true,
    maxSchwiegergrad: 1,
    verstorbeneSichtbar: true,
    bearbeitenBlutsgrad: 0,
    bearbeitenSchwiegergrad: 0,
    bearbeitenVerstorbene: true,
  });
  const [sucher, setSucher] = useState(false);

  const anlegen = useMutation({
    mutationFn: () => api.benutzerAnlegen(werte),
    onSuccess: (d) => {
      onToken(d.token, werte.anzeigename);
      void client.invalidateQueries({ queryKey: ["benutzerListe"] });
      onFertig();
    },
  });

  const setzen = (teil: Aenderung) => setWerte((alt) => ({ ...alt, ...teil }));

  return (
    <div className="rounded-lg border border-black/10 p-4 dark:border-white/10">
      <h2 className="font-medium">Neuer Zugang</h2>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-black/60 dark:text-white/60">Anzeigename</span>
          <input
            autoFocus
            value={werte.anzeigename}
            onChange={(e) => setzen({ anzeigename: e.target.value })}
            className={feld + " mt-1 w-full"}
          />
        </label>
        <label className="block text-sm">
          <span className="text-black/60 dark:text-white/60">E-Mail</span>
          <input
            type="email"
            value={werte.email}
            onChange={(e) => setzen({ email: e.target.value })}
            className={feld + " mt-1 w-full"}
          />
        </label>
        <label className="block text-sm">
          <span className="text-black/60 dark:text-white/60">Rolle</span>
          <select
            value={werte.rolle}
            onChange={(e) => setzen({ rolle: e.target.value as Rolle })}
            className={feld + " mt-1 w-full"}
          >
            {ROLLEN.map((r) => (
              <option key={r} value={r}>
                {ROLLE_KURZ[r]}
              </option>
            ))}
          </select>
        </label>
        <div className="text-sm">
          <span className="text-black/60 dark:text-white/60">Person im Stammbaum</span>
          <div className="mt-1 flex items-center gap-2">
            {werte.personId ? (
              <span>Person {werte.personId}</span>
            ) : (
              <span className="text-black/40 dark:text-white/40">keine</span>
            )}
            <button onClick={() => setSucher(!sucher)} className={knopf}>
              {werte.personId ? "ändern" : "verknüpfen"}
            </button>
          </div>
          {sucher && (
            <div className="mt-2">
              <PersonSucher
                onWahl={(p) => {
                  setzen({ personId: p.id });
                  setSucher(false);
                }}
                onAbbrechen={() => setSucher(false)}
              />
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <Regeln
          werte={{
            maxBlutsgrad: werte.maxBlutsgrad ?? 3,
            schwiegerSichtbar: werte.schwiegerSichtbar ?? true,
            maxSchwiegergrad: werte.maxSchwiegergrad ?? 1,
            verstorbeneSichtbar: werte.verstorbeneSichtbar ?? true,
          }}
          setzen={setzen}
        />
        {werte.rolle === "bearbeiter" && (
        <RegelnBearbeiten
          werte={{
            maxBlutsgrad: werte.maxBlutsgrad ?? 3,
            schwiegerSichtbar: werte.schwiegerSichtbar ?? true,
            maxSchwiegergrad: werte.maxSchwiegergrad ?? 1,
            bearbeitenBlutsgrad: werte.bearbeitenBlutsgrad ?? 0,
            bearbeitenSchwiegergrad: werte.bearbeitenSchwiegergrad ?? 0,
            bearbeitenVerstorbene: werte.bearbeitenVerstorbene ?? true,
          }}
          setzen={setzen}
        />
        )}
      </div>

      {anlegen.error && (
        <p className="mt-3 text-sm text-red-700">{(anlegen.error as Error).message}</p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          onClick={() => anlegen.mutate()}
          disabled={anlegen.isPending || !werte.email || !werte.anzeigename}
          className="rounded-lg bg-black px-3 py-2 text-sm text-white disabled:opacity-40 dark:bg-white dark:text-black"
        >
          {anlegen.isPending ? "legt an …" : "anlegen"}
        </button>
        <button onClick={onFertig} className={knopf}>
          abbrechen
        </button>
      </div>
      <p className="mt-2 text-xs text-black/40 dark:text-white/40">
        Ein Passwort wird hier nicht gesetzt. Nach dem Anlegen bekommst du einen Einladungslink,
        mit dem die Person ihr Passwort selbst setzt.
      </p>
    </div>
  );
}

export default function Benutzer() {
  useTitel("Zugänge");
  const [offen, setOffen] = useState<number | null>(null);
  const [neu, setNeu] = useState(false);
  const [link, setLink] = useState<{ token: string; name: string } | null>(null);

  const { data: ich } = useQuery({ queryKey: ["ich"], queryFn: api.ich, staleTime: 5 * 60_000 });
  const { data, isPending, error } = useQuery({
    queryKey: ["benutzerListe"],
    queryFn: api.benutzerListe,
  });

  if (error) {
    return (
      <p className="text-red-700">
        {(error as Error).message || "Die Benutzerliste konnte nicht geladen werden."}
      </p>
    );
  }

  const liste = data?.benutzer ?? [];

  return (
    <div>
      {link && (
        <Einladungslink token={link.token} name={link.name} onFertig={() => setLink(null)} />
      )}

      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Benutzer</h1>
        {!neu && (
          <button onClick={() => setNeu(true)} className={knopf}>
            Zugang anlegen
          </button>
        )}
      </div>

      {neu && (
        <div className="mb-4">
          <Anlegen
            onToken={(token, name) => setLink({ token, name })}
            onFertig={() => setNeu(false)}
          />
        </div>
      )}

      {isPending && <p className="text-black/40">lädt …</p>}

      <ul className="divide-y divide-black/5 dark:divide-white/10">
        {liste.map((b) => (
          <li key={b.id} className={b.aktiv ? "" : "opacity-50"}>
            <button
              onClick={() => setOffen(offen === b.id ? null : b.id)}
              className="flex w-full items-center gap-3 px-1 py-2 text-left hover:bg-black/[0.03] dark:hover:bg-white/5"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate">
                  {b.anzeigename}
                  <span className="ml-2 rounded bg-black/10 px-1.5 py-0.5 text-[11px] dark:bg-white/15">
                    {ROLLE_KURZ[b.rolle]}
                  </span>
                  {b.id === ich?.id && (
                    <span className="ml-2 text-[11px] text-black/40 dark:text-white/40">
                      (du selbst)
                    </span>
                  )}
                  {!b.aktiv && (
                    <span className="ml-2 rounded bg-black/10 px-1.5 py-0.5 text-[11px] dark:bg-white/15">
                      abgeschaltet
                    </span>
                  )}
                  {!b.hatPasswort && (
                    <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[11px]">
                      {b.offeneEinladung ? "Einladung offen" : "kein Passwort"}
                    </span>
                  )}
                </span>
                <span className="block truncate text-xs text-black/45 dark:text-white/45">
                  {b.email}
                  {b.person && ` · ${b.person.vorname} ${b.person.famname}`}
                  {` · sieht Grad ${b.maxBlutsgrad}`}
                  {!b.schwiegerSichtbar && " ohne Angeheiratete"}
                  {!b.verstorbeneSichtbar && " ohne Verstorbene"}
                  {b.rolle === "bearbeiter" &&
                    ` · ändert ${
                      b.bearbeitenBlutsgrad > 0 ? `Lebende bis Grad ${b.bearbeitenBlutsgrad}` : ""
                    }${b.bearbeitenBlutsgrad > 0 && b.bearbeitenVerstorbene ? " und " : ""}${
                      b.bearbeitenVerstorbene ? "Verstorbene" : ""
                    }${
                      b.bearbeitenBlutsgrad === 0 && !b.bearbeitenVerstorbene ? "nur Eigenes" : ""
                    }`}
                </span>
              </span>
              <span className="shrink-0 text-xs text-black/40 dark:text-white/40">
                zuletzt da: {datumKnapp(b.letzterLoginAm)}
              </span>
            </button>
            {offen === b.id && (
              <Bearbeiten
                b={b}
                istIch={b.id === ich?.id}
                onToken={(token, name) => setLink({ token, name })}
                onFertig={() => setOffen(null)}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
